import { useEffect, useMemo, useRef } from 'react'

import {
  ComparisonPatch,
  ComparisonWithSource,
  useComparison,
} from '@sketch-hq/sketch-web-renderer'

import { EntityChange } from '../../layerTypes'

interface ComparePatchesProps {
  /** The worker's reply, holding the version the canvas is not showing. */
  comparison: ComparisonWithSource
  /** What to draw from it: the entities this version lacks. */
  absentEntities: readonly EntityChange[]
  /** What to draw of the other version, or null - see `modePatch`. */
  patch?: ComparisonPatch | null
}

/**
 * Has the canvas draw, from the other version, the frames and Sections the
 * version on screen lacks, and its version of whatever is selected. Must
 * render inside the canvas, where the renderer hooks are.
 *
 * Mounted only while there is a comparison, so leaving one is what takes the
 * drawing away rather than a branch in here.
 */
export function ComparePatches({
  comparison,
  absentEntities,
  patch,
}: ComparePatchesProps) {
  const { set, clear } = useComparison()

  const patches = useMemo<ComparisonPatch[]>(() => {
    const inserts = absentEntities.map<ComparisonPatch>(entity => ({
      kind: 'insert',
      id: entity.uuid,
    }))

    return patch ? [...inserts, patch] : inserts
  }, [absentEntities, patch])

  // Not cleared between changes: the canvas reconciles patches against the
  // source it holds, and clearing would make it load the other version again.
  useEffect(() => {
    set({ source: comparison.source, patches })
  }, [comparison, patches, set])

  // Through a ref, so that only going away clears - a new `clear` would
  // otherwise take the drawing with it.
  const clearRef = useRef(clear)
  clearRef.current = clear

  useEffect(() => () => clearRef.current(), [])

  return null
}
